/**
 * バックエンドから取得したUTC日時文字列をユーザーのローカル（JST）時刻の文字列に変換します。
 * 
 * FastAPIのデフォルトではISO文字列の末尾にZ（UTC）が付与されない場合があるため、
 * そのまま `new Date("2024-01-01T12:00:00")` とするとローカル時間の12時として解釈されてしまいます。
 * この関数では末尾に 'Z' を補完してUTC時間であることを明示してから変換します。
 * 
 * @param {string} utcDateString - APIから取得した日時文字列 (例: "2024-01-01T12:00:00")
 * @returns {string} - JSTに変換された表示用文字列 (例: "2024/1/1 21:00:00")
 */
export const formatToJST = (utcDateString) => {
    if (!utcDateString) return '';
    
    // 既にZやタイムゾーン情報が含まれているかチェック
    const safeDateString = utcDateString.endsWith('Z') || utcDateString.includes('+')
        ? utcDateString 
        : `${utcDateString}Z`;
        
    return new Date(safeDateString).toLocaleString('ja-JP');
};

const JST_MONTH_DAY_TIME = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

const JST_DATE_TIME = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/**
 * バックエンドの UTC 日時文字列を Date にする。時差の印 (Z・+09:00 等) が無いときは UTC として読む
 * (formatToJST と同じ読み方)。
 *
 * @param {string} utcDateString
 * @returns {Date|null} 解釈できない場合は null
 */
export const parseUtcDate = (utcDateString) => {
    if (!utcDateString || typeof utcDateString !== 'string') return null;

    const safeDateString = utcDateString.endsWith('Z') || utcDateString.includes('+')
        ? utcDateString
        : `${utcDateString}Z`;
    const date = new Date(safeDateString);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * バックエンドの UTC 日時文字列を「9/9 12:17」の形 (JST) にする。
 *
 * formatToJST と違い、実行環境のタイムゾーンに依らず Asia/Tokyo で表示する
 * (画面レイアウト_SCR-M-001.md §4.6.2 の取込日時。Issue #779)。
 *
 * @param {string} utcDateString - APIから取得した日時文字列 (例: "2026-09-09T03:17:00")
 * @returns {string} - 例: "9/9 12:17"。解釈できない場合は空文字
 */
export const formatToJSTMonthDayTime = (utcDateString) => {
    const date = parseUtcDate(utcDateString);
    if (!date) return '';

    const parts = Object.fromEntries(
        JST_MONTH_DAY_TIME.formatToParts(date).map((part) => [part.type, part.value])
    );
    return `${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`;
};

/**
 * バックエンドの UTC 日時文字列を「2026/10/01 14:30」の形 (JST・年から・各 2 桁) にする。
 *
 * 推薦の実行日時 (画面レイアウト_SCR-M-001.md §4.2 DP-M-015、Issue #710)。何日も前・前年の結果も
 * 表示するため年から出す。実行環境のタイムゾーンに依らず Asia/Tokyo で表示する。
 *
 * @param {string} utcDateString - APIから取得した日時文字列 (例: "2026-10-01T05:30:00")
 * @returns {string} - 例: "2026/10/01 14:30"。解釈できない場合は空文字
 */
export const formatToJSTDateTime = (utcDateString) => {
    const date = parseUtcDate(utcDateString);
    if (!date) return '';

    const parts = Object.fromEntries(
        JST_DATE_TIME.formatToParts(date).map((part) => [part.type, part.value])
    );
    return `${parts.year}/${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`;
};
